import WebFont from 'webfontloader';
import { SELECTION_STYLE_PROPERTIES, TEXT_CASE } from '../constants';
import { isEmpty, isPpTextObject } from '@/common/utils';
import { mapObject, shallowObjectEqual } from './util';
import { isArray, isNull, isObject, toArray, uniqBy } from 'lodash';

const createDummyElement = (text, options) => {
  const element = document.createElement('div');
  const textNode = document.createTextNode(text);

  element.appendChild(textNode);

  element.style.fontFamily = options.font;
  element.style.fontSize = options.fontSize;
  element.style.fontWeight = options.fontWeight;
  element.style.lineHeight = options.lineHeight;
  element.style.position = 'absolute';
  element.style.visibility = 'hidden';
  element.style.left = '-999px';
  element.style.top = '-999px';
  element.style.width = options.width;
  element.style.height = 'auto';
  element.style.wordBreak = options.wordBreak;

  document.body.appendChild(element);

  return element;
};

const destroyElement = element => {
  element.parentNode.removeChild(element);
};

const cache = {};

export default (text, options = {}) => {
  const cacheKey = JSON.stringify({ text: text, options: options });

  if (cache[cacheKey]) {
    return cache[cacheKey];
  }

  options.font = options.font || 'Times';
  options.fontSize = options.fontSize || '16px';
  options.fontWeight = options.fontWeight || 'normal';
  options.lineHeight = options.lineHeight || 'normal';
  options.width = options.width || 'auto';
  options.wordBreak = options.wordBreak || 'normal';

  const element = createDummyElement(text, options);

  const size = {
    width: element.offsetWidth,
    height: element.offsetHeight
  };

  destroyElement(element);

  cache[cacheKey] = size;

  return size;
};

export const measureTextWidth = (canvas, text, options) => {
  const ctx = canvas.getContext('2d');
  ctx.save();

  if (!options.textCase) options.textCase = TEXT_CASE.NONE;

  const textSplit = text.split('\n').map(value => {
    ctx.font = `${options.fontWeight} ${options.fontSize} ${options.fontFamily}`;
    const transformedText = transformTextCase(value, options.textCase);
    const { width } = ctx.measureText(transformedText);
    return width;
  });
  ctx.restore();
  return Math.max(...textSplit);
};

export const transformTextCase = (string, textCase = TEXT_CASE.NONE) => {
  const transformOpt = {
    [TEXT_CASE.UPPER]: str => str.toUpperCase(),
    [TEXT_CASE.CAPITALIZE]: str => str.charAt(0).toUpperCase() + str.slice(1),
    [TEXT_CASE.LOWER]: str => str.toLowerCase(),
    [TEXT_CASE.NONE]: str => str
  };
  return transformOpt[textCase](string);
};

export const loadFonts = (fonts, ignoreCheck = false) => {
  return new Promise((resolve, reject) => {
    let unavailableFonts = fonts.filter(
      font => !document.fonts.check(`72px ${font}`)
    );
    if (ignoreCheck) {
      unavailableFonts = fonts;
    }
    if (isEmpty(unavailableFonts)) {
      resolve();
      return;
    }
    if (ignoreCheck) {
      const listPromise = [];
      unavailableFonts.forEach(font => {
        listPromise.push(
          new Promise((res, rej) => {
            WebFont.load({
              google: {
                families: [font]
              },
              active: res,
              inactive: rej,
              timeout: 10 * 1000 // 10 sec
            });
          })
        );
      });
      Promise.allSettled(listPromise)
        .then(resolve)
        .catch(reject);
    } else {
      WebFont.load({
        google: {
          families: unavailableFonts
        },
        active: resolve,
        inactive: reject,
        timeout: 10 * 1000 // 10 sec
      });
    }
  });
};

/**
 * Get css style of item base on its style
 *
 * @param   {Object} style  style of item
 * @returns {Object}        css style of item
 */
export const getCssTextStyle = cssStyle => {
  if (isEmpty(cssStyle)) return {};

  const mapRules = {
    data: {
      fontSize: {
        name: 'fontSize',
        parse: value => {
          const fontSize = parseInt(value, 10) / 3;

          const calFontSize = (() => {
            if (fontSize > 50) return 50;

            return fontSize < 16 ? 16 : fontSize;
          })();

          return `${calFontSize}px`;
        }
      }
    },
    restrict: []
  };

  return mapObject(cssStyle, mapRules);
};

// Return current canvas is edit Text box
export const isEditTextbox = () => {
  const currentCanvas = window.printCanvas || window.digitalCanvas;
  if (!currentCanvas) {
    return false;
  }
  return currentCanvas.getActiveObject().type === 'textbox';
};

export const getPropertyForSelectionStyle = prop => {
  const propertySelection = {};
  for (const key in prop) {
    if (
      Object.hasOwnProperty.call(prop, key) &&
      SELECTION_STYLE_PROPERTIES.includes(key)
    ) {
      propertySelection[key] = prop[key];
    }
  }
  return propertySelection;
};

export const convertObjectStyleToArray = styles => {
  if (isArray(styles)) {
    return styles;
  }

  if (!isObject(styles)) {
    return [];
  }
  // because we have 2 level object style => convert once more time to make only 1 level array
  const twoLevelArray = toArray(styles);
  const finalArr = [];
  for (const objStyle of twoLevelArray) {
    finalArr.push(...toArray(objStyle));
  }
  return finalArr;
};

export const getUniqueStylesWithPropertyName = (propertyName, styles = []) => {
  const indexOfPropertyName = styles.findIndex(item => item[propertyName]);
  if (indexOfPropertyName < 0) {
    return [];
  }
  return uniqBy(styles, propertyName);
};

export const isFabricTextbox = obj => obj?.type === 'textbox';
export const convertSelectionStylesToArray = obj => {
  let selectionStyles = [];
  let arrayObject = [];
  let style = [];
  const lengthSelectionStyles = Object.entries(obj.selectionStyles).length;
  const arrayKeySelectionStyles = Object.entries(obj.selectionStyles).map(
    ([k]) => +k
  );
  for (let i = 0; i < lengthSelectionStyles; i++) {
    const arrayStyle = Object.entries(
      obj.selectionStyles[arrayKeySelectionStyles[i]]
    ).map(([k, v]) => [+k, v]);
    if (isEmpty(arrayStyle)) {
      selectionStyles[arrayKeySelectionStyles[i]] = [];
      continue;
    }
    style.push(arrayStyle[0][0]);
    if (arrayStyle.length === 1) {
      style.push(arrayStyle[0][0]);
      style.push(arrayStyle[0][1]);
      arrayObject.push(style);
      style = [];
      selectionStyles[arrayKeySelectionStyles[i]] = arrayObject;
      arrayObject = [];
      continue;
    }
    for (let x = 0; x < arrayStyle.length - 1; x++) {
      const isAfter = arrayStyle[x][0] + 1 === arrayStyle[x + 1][0];
      const isSameValue = shallowObjectEqual(
        arrayStyle[x][1],
        arrayStyle[x + 1][1]
      );
      if (!isAfter || !isSameValue) {
        style.push(arrayStyle[x][0]);
        style.push(arrayStyle[x][1]);
        arrayObject.push(style);
        style = [];
        style.push(arrayStyle[x + 1][0]);
      }
      if (x === arrayStyle.length - 2) {
        style.push(arrayStyle[x + 1][0]);
        style.push(arrayStyle[x][1]);
        arrayObject.push(style);
        style = [];
      }
    }
    selectionStyles[arrayKeySelectionStyles[i]] = arrayObject;
    arrayObject = [];
  }
  return selectionStyles;
};

export const combineTextSelectionStyles = objects => {
  if (isEmpty(objects)) return;
  objects.forEach(o => {
    if (
      isPpTextObject(o) &&
      !isEmpty(o.selectionStyles) &&
      isObject(o.selectionStyles)
    ) {
      const selectionStyles = convertSelectionStylesToArray(o);
      o.selectionStyles = selectionStyles;
    }
  });
};

export const convertSelectionStylesToObject = obj => {
  let selectionStylesObjects = {};

  obj.selectionStyles.forEach((sStyle, index) => {
    if (isNull(sStyle)) return;
    selectionStylesObjects[index] = {};
    sStyle.forEach(s => {
      const begin = s[0];
      const end = s[1];
      const value = s[2];
      for (let i = begin; i <= end; i++) {
        selectionStylesObjects[index][i] = { ...value };
      }
    });
  });
  return selectionStylesObjects;
};

export const separateTextSelectionStyles = objects => {
  if (isEmpty(objects)) return;
  objects.forEach(o => {
    if (isPpTextObject(o) && isArray(o.selectionStyles)) {
      const selectionStyles = convertSelectionStylesToObject(o);
      o.selectionStyles = selectionStyles;
    }
  });
};
